<template>
  <!-- CD-12 ACCOUNT PAYMENT DETAILS -->
  <div class="space-y-4 w-full">
    <div v-if="showAddCardForm">
      <AddCardInline @card:added="handleCardAdded" @card:cancelled="handleCardCancelled" />
      <div class="flex justify-end gap-3 mt-4">
        <Button variant="outline" @click="handleCardCancelled">Cancel</Button>
      </div>
    </div>
    <div v-else>
      <div
        v-for="card in cards"
        :key="card._id"
        class="border rounded-lg p-4 flex items-center justify-between mb-2 relative md:min-w-[512px] md:max-w-[512px]"
        :class="card.defaultCard ? 'bg-brand-50 border-brand-300' : 'border-gray-200'"
      >
        <div
          v-if="loadingCardId === card._id"
          class="absolute inset-0 bg-white/80 rounded-lg flex items-center justify-center z-10"
        >
          <LayupSpinner :size="8" container-classes="" />
        </div>
        <div class="flex items-center gap-3">
          <div class="w-[46px] h-[32px] flex items-center justify-center">
            <component :is="checkCardBrand(card).icon" />
          </div>
          <div>
            <p class="text-[14px] text-brand-800 font-medium">{{ checkCardBrand(card).customDescription }}</p>
            <p class="text-[14px] text-brand-600">Expiry {{ card.expiryMonth }}/{{ card.expiryYear }}</p>
            <p v-if="card.defaultCard" class="text-[14px] text-brand-500 mt-2 font-semibold">DEFAULT</p>
            <p v-if="isCardExpired(card)" class="text-[14px] text-grey-light-500 mt-2">EXPIRED</p>
          </div>
        </div>
        <DropdownMenu class="relative -top-[100px]">
          <DropdownMenuTrigger as-child>
            <Button variant="ghost" size="icon">
              <MoreVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem v-if="!card.defaultCard && !isCardExpired(card)" @click="$emit('set-default', card._id!)">
              Set as default
            </DropdownMenuItem>
            <DropdownMenuItem @click="$emit('remove-card', card._id!)">Remove</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div class="flex md:max-w-[512px]" :class="isDesktop ? 'justify-end' : 'justify-start'">
        <Button
          variant="link"
          class="text-brand-700 font-semibold px-0"
          :disabled="cards.length >= 3"
          @click="showAddCardForm = true"
          data-testid="add-card"
        >
          <Plus />
          Add Card
        </Button>
      </div>
      <p v-if="cards.length >= 3" class="text-xs text-grey-light-500">Maximum of 3 cards reached</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import MasterCardIcon from '@/components/icons/MasterCardIcon.vue';
import VisaCardIcon from '@/components/icons/VisaCardIcon.vue';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useBreakpoint } from '@/utils/useBreakPoint';
import dayjs from 'dayjs';
import { MoreVertical, Plus } from 'lucide-vue-next';
import { ref } from 'vue';
import type { ModelCardResponse } from '@layup/api-sdk';
import AddCardInline from './AddCardInline.vue';

const { isDesktop } = useBreakpoint();

defineProps<{
  cards: ModelCardResponse[];
  loadingCardId: string | null;
}>();

const emit = defineEmits<{
  'set-default': [cardId: string];
  'remove-card': [cardId: string];
  'card-added': [];
}>();

const showAddCardForm = ref(false);

const handleCardAdded = () => {
  showAddCardForm.value = false;
  emit('card-added');
};

const handleCardCancelled = () => {
  showAddCardForm.value = false;
};

const isCardExpired = (card: ModelCardResponse) => {
  const input = dayjs(`${card.expiryYear}-${card.expiryMonth}`, 'YYYY-MM');
  const now = dayjs();
  return input.isBefore(now, 'month');
};

const checkCardBrand = (card: ModelCardResponse) => {
  switch (card.cardType) {
    case 'VISA':
      return {
        icon: VisaCardIcon,
        customDescription: `Visacard ending with ${card.cardDigits}`,
      };
    case 'MASTER':
      return {
        icon: MasterCardIcon,
        customDescription: `Mastercard ending with ${card.cardDigits}`,
      };
    default:
      return {
        icon: null,
        customDescription: null,
      };
  }
};
</script>
